/**
 * Browser pixel loader (P8-F7b, snaps lock #1).
 *
 * Ставит на страницу базовые вставки рекламных пикселей — Meta (`fbq`) и
 * TikTok (`ttq`), — чтобы usePurchasePixel/playerPixelEvents нашли глобальные
 * функции и смогли отправлять события. Без этого браузерная половина отправки
 * не работает вовсе (вызовы уходят в undefined).
 *
 * - Consent fail-closed (P8-F9): ничего не грузится, пока `consent !== 'granted'`.
 *   Арбитр согласия — сервер (success-data payload), он главнее кук. Правило
 *   ОДНО на обе сети: отдельной ветки для TikTok здесь нет (WEB-2180 п.4).
 * - Идемпотентность: каждый идентификатор пикселя инициализируется не более
 *   одного раза на загрузку страницы.
 * - Просмотр страницы: Meta его здесь НЕ шлёт (состав событий воронки —
 *   WEB-2182), TikTok шлёт `ttq.page()` — это часть его базовой вставки.
 */

interface Attribution {
  readonly metaPixelId?: string;
  readonly ttPixelId?: string;
  /**
   * WEB-2181 Б — ГОТОВЫЕ имена событий сети по видам («вид → имя»), как их
   * отдаёт публичный ответ квиза/экрана оплаты. Имена уже применены сервером
   * (клиентское переименование учтено); фронт их не вычисляет.
   */
  readonly events?: {
    readonly meta?: Readonly<Record<string, string>>;
    readonly tiktok?: Readonly<Record<string, string>>;
  };
}

const loadedMeta = new Set<string>();
const loadedTikTok = new Set<string>();

/**
 * Заглушка Meta по официальному образцу: сама функция + поля, которые
 * выставляет вставка из документации.
 */
type FbqStub = ((...args: unknown[]) => void) & {
  /** Дописывается САМОЙ библиотекой после загрузки (не нами). */
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
};

/** Заглушка TikTok — массив, в который складываются вызовы до загрузки. */
type TtqStub = unknown[] & {
  methods?: readonly string[];
  setAndDefer?: (target: TtqStub | unknown[], method: string) => void;
  instance?: (pixelId: string) => unknown[];
  load?: (pixelId: string, options?: Record<string, unknown>) => void;
  page?: (...args: unknown[]) => void;
  track?: (...args: unknown[]) => void;
  /** WEB-2147 п.2: штатный отзыв согласия из базовой вставки TikTok. */
  revokeConsent?: (...args: unknown[]) => void;
  _i?: Record<string, unknown[]>;
  _t?: Record<string, number>;
  _o?: Record<string, Record<string, unknown>>;
};

/**
 * Чужая вставка ttq (GTM, ручной сниппет клиента): её форму мы не знаем и
 * полагаться можем только на официальный `load`.
 */
type ForeignTtq = {
  load?: (pixelId: string, options?: Record<string, unknown>) => void;
};

type Win = typeof globalThis & {
  fbq?: FbqStub;
  _fbq?: FbqStub;
  ttq?: TtqStub | ForeignTtq;
  TiktokAnalyticsObject?: string;
};

import {
  fireMetaPageView,
  setPlayerPixelEventNames,
  setPlayerTikTokEventNames,
  setPlayerTrackingRevoked,
} from './playerPixelEvents';
import { isGlobalPrivacyControlEnabled } from './euConsent';

const META_LIB_URL = 'https://connect.facebook.net/en_US/fbevents.js';
const TIKTOK_LIB_URL = 'https://analytics.tiktok.com/i18n/pixel/events.js';

export function loadPixels(
  attribution: Attribution | undefined,
  consent: string | null | undefined,
): void {
  // Fail-closed: only granted consent may load tracking pixels.
  if (consent !== 'granted') return;
  // WEB-2162 п.4: сигнал браузера Global Privacy Control — отказ от ПЕРЕДАЧИ
  // данных третьим лицам, а пиксель и есть передача третьему лицу. Явное
  // «Принять» на баннере сигнал не перекрывает: общее возражение, выраженное
  // человеком в браузере, частным согласием на нашей странице не отменяется.
  // Собственная аналитика при этом работает (см. trackEvent.ts).
  if (isGlobalPrivacyControlEnabled()) return;
  if (!attribution) return;
  if (typeof window === 'undefined') return;

  // WEB-2181 Б: имена событий кладутся ДО установки пикселя — первое же
  // событие должно уйти под именем, которое назвал сервер. Блока нет (ответ
  // закеширован до карточки) — остаются прежние имена плеера.
  setPlayerPixelEventNames(attribution.events?.meta);
  // WEB-2409 п.1: имена TikTok — тем же путём и из того же ответа. Блока нет —
  // TikTok из браузера событий воронки не шлёт (запасных имён у него нет).
  setPlayerTikTokEventNames(attribution.events?.tiktok);
  // Сюда доходят только при действующем согласии (проверки выше): новое
  // «Принять» после «Отклонить» снова открывает отправку событий воронки.
  setPlayerTrackingRevoked(false);

  // Аналитика никогда не мешает покупателю: сбой установки одного пикселя не
  // имеет права ни уронить отрисовку страницы, ни отменить второй пиксель.
  safely(() => {
    if (attribution.metaPixelId) loadMetaPixel(attribution.metaPixelId);
  });
  safely(() => {
    if (attribution.ttPixelId) loadTikTokPixel(attribution.ttPixelId);
  });
}

function safely(run: () => void): void {
  try {
    run();
  } catch {
    /* установка пикселя не удалась — страница работает дальше */
  }
}

function injectScript(src: string): void {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

/**
 * WEB-1364 п.4 — вставка Meta приведена к официальному образцу.
 *
 * Сверено 17.09.2026 с живой документацией
 * (developers.facebook.com/docs/meta-pixel/get-started) и с САМОЙ библиотекой
 * `connect.facebook.net/en_US/fbevents.js`, скачанной в тот же день:
 *  - библиотека НЕ подменяет `window.fbq` собой, а дописывает загруженной
 *    заглушке метод: `function at(e){e.instance=…;e.callMethod=…;…}` — поэтому
 *    точкой входа навсегда остаётся наша заглушка;
 *  - очередь заглушки библиотека разбирает разово при загрузке:
 *    `for(;e.fbq.queue&&e.fbq.queue.length;){…We.apply(e.fbq,t)}`.
 *
 * Отсюда дефект прежней версии: она клала вызов в очередь ВСЕГДА, и всё, что
 * отправлено после загрузки библиотеки (просмотр экрана на каждой смене экрана,
 * лид при вводе почты), оседало в очереди, которую больше никто не разбирает.
 * Ветка `callMethod ? callMethod(...) : queue.push(...)` — ровно она это и чинит.
 *
 * `fbq('track','PageView')` официальной вставки здесь намеренно НЕ вызывается:
 * состав событий воронки — отдельная карточка WEB-2182.
 */
function loadMetaPixel(pixelId: string): void {
  if (loadedMeta.has(pixelId)) return;
  loadedMeta.add(pixelId);

  const w = window as Win;
  if (!w.fbq) {
    const n: FbqStub = function (...args: unknown[]) {
      if (n.callMethod) {
        // Унаследованный долг линта (`prefer-spread`), поправлен заодно:
        // вызов через член объекта даёт тот же `this`, что и `.apply(n, …)`.
        n.callMethod(...args);
        return;
      }
      (n.queue = n.queue || []).push(args);
    };
    w.fbq = n;
    if (!w._fbq) w._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    injectScript(META_LIB_URL);
  }
  // WEB-2147 п.1 — ВЫКЛЮЧИТЬ АВТОМАТИЧЕСКИЕ СОБЫТИЯ SDK.
  //
  // У Meta этот режим включён по умолчанию: загруженный `fbevents.js` сам
  // вешает слушатели на ВСЕ кнопки страницы и шлёт `SubscribedButtonClick`
  // вместе с текстом кнопки в открытом виде. В приёмке 06.09 это поймали
  // живьём — в Facebook уехало слово «Отклонить», то есть сам факт отказа
  // человека от отслеживания. Это единственная дыра разбора согласия, по
  // которой регуляторы штрафуют именно за эту функцию (три дела шведского
  // органа; в двух из трёх она была включена непреднамеренно, и это не
  // смягчило ответственность). Данные автоматических событий мы не
  // используем нигде, поэтому цена выключения нулевая.
  //
  // ⚠ ПОРЯДОК ВАЖЕН: после `init` настройка к этому идентификатору уже не
  // применится, поэтому вызов стоит строго выше.
  w.fbq('set', 'autoConfig', false, pixelId);
  w.fbq('init', pixelId);
  // WEB-2182 п.6: канонический просмотр страницы сразу после инициализации.
  // Путь одноразовый (playerPixelEvents), поэтому на квизе, где просмотр шлёт
  // ещё и сама страница, второго события не будет.
  void fireMetaPageView();
}

/**
 * WEB-2180 — базовая вставка TikTok. До этой карточки на её месте стоял
 * комментарий-заглушка (TikTok был отложен в WEB-162), и браузерная половина
 * отправки в TikTok не работала ни на одной странице, хотя кабинет показывал
 * пиксель подключённым.
 *
 * Сверено 17.09.2026. Прозу портала документации вычитать не удалось
 * (business-api.tiktok.com отдаёт пустую оболочку одностраничного приложения),
 * поэтому форма сверена по более твёрдому источнику — по САМОЙ библиотеке
 * `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=<ID>&lib=ttq`,
 * скачанной в тот же день (ответ 200). Она читает ровно те ключи, которые
 * выставляет официальный базовый сниппет:
 *  - `window[window.TiktokAnalyticsObject]` — имя глобальной переменной;
 *  - `n._i[pixelCode]` — реестр загруженных пикселей (`n._i||(n._i={})`,
 *    `n._i[v]||(n._i[v]=[])`);
 *  - `_o` (настройки пикселя, читается как `g(e,"_o")`) и `_t` (метка времени).
 * Отсутствие любого из них означает, что библиотека не найдёт наш пиксель.
 *
 * Имена событий здесь НЕ задаются и не меняются: их даёт сервер
 * (WEB-1319, `usePurchasePixel`), склейка браузер↔s2s держится на совпадении
 * имени и ключа. `ttq.page()` — часть базовой вставки, это единственное
 * событие, которое даёт сам загрузчик.
 */
const TIKTOK_METHODS: readonly string[] = [
  'page',
  'track',
  'identify',
  'instances',
  'debug',
  'on',
  'off',
  'once',
  'ready',
  'alias',
  'group',
  'enableCookie',
  'disableCookie',
  'holdConsent',
  'revokeConsent',
  'grantConsent',
];

function loadTikTokPixel(pixelId: string): void {
  if (loadedTikTok.has(pixelId)) return;
  loadedTikTok.add(pixelId);

  const w = window as Win;

  // На странице уже стоит ЧУЖАЯ вставка ttq (GTM, ручной сниппет клиента):
  // её форма не наша, подменять её нельзя — свой пиксель добавляем её же
  // способом (`ttq.load` официального сниппета принимает несколько
  // идентификаторов), а если способа нет — не трогаем ничего.
  const present = w.ttq;
  if (present && !Array.isArray(present)) {
    present.load?.(pixelId);
    return;
  }

  /**
   * Ревизия 43.1 п.9 — ЧЬЯ ЭТО ВСТАВКА.
   *
   * Официальный сниппет клиента (GTM, ручной код) до загрузки библиотеки
   * создаёт `window.ttq` МАССИВОМ и сам зовёт `ttq.page()`. Массив не уходит в
   * ветку «чужая вставка» выше, поэтому раньше мы в конце безусловно звали
   * `page()` ещё раз — второй Pageview за загрузку страницы, то есть завышенный
   * счёт просмотров в кабинете клиента. Базовое событие вставки зовёт только
   * тот, кто вставку поставил.
   */
  const ourStub = present === undefined;

  w.TiktokAnalyticsObject = 'ttq';
  const ttq: TtqStub = present ?? ([] as TtqStub);
  w.ttq = ttq;

  if (!ttq.methods) {
    ttq.methods = TIKTOK_METHODS;
    // До загрузки библиотеки каждый метод складывает вызов в сам массив ttq —
    // библиотека разбирает его после загрузки.
    ttq.setAndDefer = (target: TtqStub | unknown[], method: string): void => {
      (target as unknown as Record<string, unknown>)[method] = (
        ...args: unknown[]
      ): void => {
        (target as unknown[]).push([method, ...args]);
      };
    };
    for (const method of TIKTOK_METHODS) ttq.setAndDefer(ttq, method);
    ttq.instance = (id: string): unknown[] => {
      const instance = ttq._i?.[id] ?? [];
      for (const method of TIKTOK_METHODS) ttq.setAndDefer?.(instance, method);
      return instance;
    };
    ttq.load = (id: string, options?: Record<string, unknown>): void => {
      ttq._i = ttq._i ?? {};
      ttq._i[id] = [];
      (ttq._i[id] as unknown[] & { _u?: string })._u = TIKTOK_LIB_URL;
      ttq._t = ttq._t ?? {};
      ttq._t[id] = Date.now();
      ttq._o = ttq._o ?? {};
      ttq._o[id] = options ?? {};
      injectScript(`${TIKTOK_LIB_URL}?sdkid=${encodeURIComponent(id)}&lib=ttq`);
    };
  }

  ttq.load?.(pixelId);
  if (ourStub) ttq.page?.();
}

/**
 * WEB-2147 п.2 — сообщить уже загруженным пикселям, что согласие отозвано.
 *
 * Выгрузить чужую библиотеку из страницы нельзя, но у обеих сетей есть
 * штатный способ сказать «больше не отправляй»: у Meta — `fbq('consent',
 * 'revoke')`, у TikTok — `revokeConsent` из её же базовой вставки (метод
 * объявлен в TIKTOK_METHODS выше). До WEB-2145 сценарий был недостижим —
 * решение изменить было нечем; теперь на публичной странице есть постоянный
 * элемент отзыва, и связка обязана быть разорвана по-настоящему.
 *
 * Зовётся из обработчика решения обеих публичных страниц, рядом с очисткой
 * атрибуционных кук. Отсюда, а не из `euConsent.ts`: там вышел бы круг
 * импортов (pixelLoader → playerPixelEvents → euConsent).
 */
export function revokeTrackingConsent(): void {
  if (typeof window === 'undefined') return;
  // WEB-2409 п.1: сами тоже перестаём звать `track` обеих сетей — штатный
  // отзыв библиотеки не повод продолжать слать ей события.
  setPlayerTrackingRevoked(true);
  const w = window as Win;
  safely(() => {
    w.fbq?.('consent', 'revoke');
  });
  safely(() => {
    const ttq = w.ttq as TtqStub | ForeignTtq | undefined;
    (ttq as TtqStub | undefined)?.revokeConsent?.();
  });
}

/** Test-only: reset idempotency guards between test cases. */
export function __resetPixelLoaderForTests(): void {
  loadedMeta.clear();
  loadedTikTok.clear();
}
